/* Copyright 2011-2020 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dijit/registry",
    "dojo/dom",
    "dojo/dom-attr",
    "mw-peermodel/PeerModelManager",
    "mw-peermodel/_PeerModelListenerMixin",
    "../constants/TypeConstants",
    "MW/toolstrip/constants/QABAuthoringModeConstants",
    "../hashes/ComponentPropertiesHash",
    "../hashes/IconPropertiesSupportingBase64",
    "../hashes/ComponentsWithNoParent",
    "MW/statusbar/ComponentsThatSupportAction",
    "MW/toolstrip/hashes/ComponentsSupportedByFooterCell",
    "MW/toolstrip/hashes/ComponentsEligibleForQAB",
    "MW/toolstrip/ToolstripUtils",
    "../../toolstrip/hashes/listOfNewWidgets",
    "MW/toolstrip/hashes/DefaultGalleryItemWidths",
    "mw-log/Log"
], function (declare, lang, registry, dom, domAttr, PeerModelManager, _PeerModelListenerMixin, TypeConstants,
             QABAuthoringModeConstants, ComponentPropertiesHash, IconPropertiesSupportingBase64, ComponentsWithNoParent,
             StatusBarComponentsThatSupportAction, ComponentsSupportedByFooterCell, ComponentsEligibleForQAB,
             ToolstripUtils, listOfNewWidgets, DefaultGalleryItemWidths, Log) {
    /**
     * <code>UIBuilder</code> provides methods to create, modify and access components<br>
     *
     * @exports MW/uiframework/UIBuilder
     *
     * @class
     */
    var modelManager =
    {

        constructor: function (args) {
            args = args || {};

            this.isMATLABDriven = args.isMATLABDriven;
            this._channel = args.channel;
            this.actionService = args.actionService;
            this.enableQABCustomization = args.enableQABCustomization;
            this.lazyLoading = args.lazyLoading;

            // This is a map of the items that need to be lazily loaded in QAB if the items within toolstrip are loaded
            // lazily. A widget defines a "groupIdForLazyLoadingQABItems" property on its action and once the items
            // within the toolstrip are loaded, clients can use uiBuilder's "lazyLoadQABItems" API to add the
            // corresponding widgets to QAB.
            //
            // For ex. Favorite Commands get lazily loaded. So, all their GalleryItems will have a common
            // groupIdForLazyLoadingQABItems = "FavoriteCommands" in their action.
            // While loading the QAB state on startup, if uiBuilder sees this property
            // 'groupIdForLazyLoadingQABItems' in its action, it stores it in this map. All the favorite commands in
            // QAB will get stored in this map in the form of an array and the key would be groupIdForLazyLoadingQABItems.
            // Thus, this.lazyLoadingQABItemsMap["FavoriteCommands"] = [itemData1, itemData2...]
            // Once, Favorite Commands is loaded, calling uiBuilder.lazyLoadQABItems("FavoriteCommands") will add
            // the corresponding QAB items to QAB.
            this.lazyLoadingQABItemsMap = {};

            this._qabAuthoringMode = args.qabAuthoringMode;
            if (!Object.keys(QABAuthoringModeConstants).some(function (key) {
                return this._qabAuthoringMode === QABAuthoringModeConstants[key];
            }.bind(this))) {
                Log.error('Expected one of the following inputs:' +
                    ' MW/toolstrip/constants/QABAuthoringModeConstants[ "' + Object.keys(QABAuthoringModeConstants).toString().replace(',', '" || "') + '" ]');
            }


            // A list of all the actions that are generated for components that are created without supplying an
            // actionId. This is maintained so that they can be cleaned up once all components using this action are
            // destroyed.
            this._autoGeneratedActions = {};
            this.tagToPeerNodeIdMap = {};
            
            // This map is maintained for performance reasons. Querying the peer model tree to
            // search for a peer node by type is expensive. This is used mainly for the top root
            // level peer nodes.
            this._typeToRootNodeMap = {};
            // This map is maintained for storing the propertySetCallback listeners to update the
            // overrides on the favorite item
            this._notInActionPropertiesSyncCallbackMap = {};

            this._setupPeerModelManager();
        },

        /* _setViewModelManager: function() {
            var galleryRoot;

            this.manager = MF0ViewModelManagerFactory.getViewModelManager({channelPrefix: this._channel});

            this.addManagerListeners();

            if (this.isMATLABDriven) {
             // this.start();
            } else {
             this._root = this.manager.setRoot(TypeConstants.ROOT);
             this._root.addChild(TypeConstants.ORPHAN_ROOT);
             this._toolstripRoot = this._root.addChild(TypeConstants.TOOLSTRIP_ROOT);
             this._root.addChild(TypeConstants.POPUP_ROOT);
             this._root.addChild(TypeConstants.QAB_ROOT);
             galleryRoot = this._root.addChild(TypeConstants.GALLERY_ROOT);
             galleryRoot.addChild(TypeConstants.GALLERY_POPUP_ROOT);
             galleryRoot.addChild(TypeConstants.GALLERY_PANEL_ROOT);
             galleryRoot.addChild(TypeConstants.GALLERY_FAVORITE_CATEGORY_ROOT);
             this._root.addChild(TypeConstants.STATUS_BAR_ROOT);
            }
        },*/

        /**
         *
         * @private
         */

        _setupPeerModelManager: function () {
            var galleryRoot;

            this.manager = PeerModelManager.getInstance(this._channel);

            this.addManagerListeners();

            if (this.isMATLABDriven) {
                this.start();
            } else {
                this._root = this.manager.setRoot(TypeConstants.ROOT);
                this._root.addChild(TypeConstants.ORPHAN_ROOT);
                this._toolstripRoot = this._root.addChild(TypeConstants.TOOLSTRIP_ROOT);
                this._root.addChild(TypeConstants.POPUP_ROOT);
                this._root.addChild(TypeConstants.QAB_ROOT);
                galleryRoot = this._root.addChild(TypeConstants.GALLERY_ROOT);
                galleryRoot.addChild(TypeConstants.GALLERY_POPUP_ROOT);
                galleryRoot.addChild(TypeConstants.GALLERY_PANEL_ROOT);
                galleryRoot.addChild(TypeConstants.GALLERY_FAVORITE_CATEGORY_ROOT);
                this._root.addChild(TypeConstants.STATUS_BAR_ROOT);
            }
        },

        /**
         * Creates a component. You can optionally provide the "parentId" along with the component's properties in the args. If a "parentId" is provided, the component is created as well as added to the parent, otherwise, you can create the component and then add it later using the "add" method.
         *
         *  @example <caption>Example usage of create.</caption>
         *  var tb = UIBuilderFactory.getInstance();
         *  var myButtonId = uiBuilder.create({
         *      type: TypeConstants.PUSH_BUTTON,
         *      tag: "MyButton",
         *      text: "Some Text",
         *      description: "I do something awesome!"
         *      });

         *  //Using Action
         * var actionService = new ActionDataService();
         * var tb = UIBuilderFactory.getInstance({
         *     actionService: actionService
         *     });
         * var action1 = actionService.addAction({
         *     text: "Some Text",//text property lives in the action(refer to the properties table for PushButton below)
         *     description: "I do something awesome!"//description property also lives in the action
         *     });
         * var myButton1Id = uiBuilder.create({
         *     type: TypeConstants.PUSH_BUTTON,
         *     tag: "MyButton1",//tag property lives in the component, since a tag should be specific to a component and does not have to be shared
         *     actionId: action1
         *     });
         * var myButton2Id = uiBuilder.create({
         *     type: TypeConstants.PUSH_BUTTON,
         *     tag: "MyButton2",//tag property lives in the component, since a tag should be specific to a component and does not have to be shared
         *     actionId: action1
         *     });
         *
         * @param {object} props The configuration of the item that needs to be added.
         * @instance
         * @source
         *

         * @returns {string} The auto generated unique id of the newly created component. You should store this in a variable. This id will be used to operate on the component using the destroy, set, get etc.
         */
        create: function (props) {
            var verifiedProperties, parentNode, peerNode, parentId, properties, actionProps;
            this._supportBase64URL(props);
            properties = lang.clone(props);

            parentId = properties.parentId;

            verifiedProperties = this._verifyProperties(properties);

            if (this._supportsAction(props.type)) {
                if (props.actionId) {
                    actionProps = this.actionService.getAction(props.actionId);
                    if (actionProps) {
                        this._supportBase64URL(actionProps);
                        this._convertPopupTagToId(actionProps);
                        //Give default values for Action properties which do not have a value
                        //Loop through component properties hash to find all the properties
                        Object.keys(ComponentPropertiesHash[props.type]).forEach(function (prop) {
                            // For all valid properties in action
                            if (!ComponentPropertiesHash[props.type][prop].notInAction) {
                                if (!this._isAValidValue(actionProps[prop], ComponentPropertiesHash[props.type][prop], ComponentPropertiesHash[props.type])) {
                                    actionProps[prop] = verifiedProperties.actionNodeProps[prop];
                                }
                            }
                        }, this);
                        //Update Action with default values added
                        this.actionService.updateAction(props.actionId, actionProps);
                    }
                } else {
                    verifiedProperties.viewNodeProps.actionId = this.actionService.addAction(verifiedProperties.actionNodeProps);
                    this._autoGeneratedActions[verifiedProperties.viewNodeProps.actionId] = true;
                }
            }

            // Decide the parent node depending on the type.
            parentNode = this._getCorrespondingParentNode(verifiedProperties.type, parentId);

            // If parent is footercell and if widget type is not one of them in ComponentsThatSupportGalleryFooter, do not create widget
            // For example, Text Area is not supported in Gallery Footer cell as TextArea spans the entire column
            if (ToolstripUtils.isAFooterCell(parentNode.getType())) {
                //use typeconstants.footercell
                //peernode getype
                //create a function in toolstriputils that return true or false for type
                if (ComponentsSupportedByFooterCell.indexOf(props.type) === -1) {
                    Log.error(props.type + " is not supported by Footer Cell");
                }
            }

            // Create the new peer node appending it to the appropriate parent.
            // g1698140: Provide the originator to the addChild() method if it was passed in.
            peerNode = parentNode.addChild(verifiedProperties.type, verifiedProperties.viewNodeProps, null, props.originator || null); 

            //  If toolstrip is getting created and if it has a parent Id, directly place the toolstrip
            if (verifiedProperties.type === "Toolstrip" && parentId) {
                //  If parent Id is passed to create method and if its type is toolstrip, call the placeat method directly
                this.placeToolstripById(peerNode.getId(), parentId);
            }

            // Do some post processing once the peer node is created depending on the type.
            this._postProcessing(peerNode);

            // Resize toolstrip since a newly created component will take extra real estate within the toolstrip.
            this._resizeToolstrip();

            return peerNode.getId();
        },
        /**
         * Gets the parent of the widget
         *  @param type The type of Widget
         * @param parentId The parent Id
         * @private
         */
        _getCorrespondingParentNode: function (type, parentId) {
            var parentNode;
            switch (type) {
            case TypeConstants.TOOLSTRIP:
                parentNode = this._getRootNodeByType(TypeConstants.TOOLSTRIP_ROOT);
                break;
            case TypeConstants.QAB:
                parentNode = this._getRootNodeByType(TypeConstants.QAB_ROOT);
                break;
            case TypeConstants.POPUP_LIST:
                if (this.exists(parentId)) {
                    parentNode = this.manager.getById(parentId);
                } else {
                    parentNode = this._getRootNodeByType(TypeConstants.POPUP_ROOT);
                }
                break;
            case TypeConstants.GALLERY_POPUP:
                parentNode = this._getRootNodeByType(TypeConstants.GALLERY_POPUP_ROOT);
                break;
            case TypeConstants.GALLERY_PANEL:
                parentNode = this._getRootNodeByType(TypeConstants.GALLERY_PANEL_ROOT);
                break;
            case TypeConstants.GALLERY_FAVORITE_CATEGORY:
                parentNode = this._getRootNodeByType(TypeConstants.GALLERY_FAVORITE_CATEGORY_ROOT);
                break;
            case TypeConstants.STATUS_BAR:
                parentNode = this._getRootNodeByType(TypeConstants.STATUS_BAR_ROOT);
                break;
            default:
                if (this.exists(parentId)) {
                    parentNode = this.manager.getById(parentId);
                } else {
                    parentNode = this._getRootNodeByType(TypeConstants.ORPHAN_ROOT);
                }
            }

            return parentNode;
        },

        _supportsAction: function(type) {
            return ToolstripUtils.supportsAction(type) || StatusBarComponentsThatSupportAction.indexOf(type) > -1;
        },

        /**
         * This function returns the id of the ancestor widget
         * @param ancestorType The type of ancestory widget. Usually it would TypeConstants.TAB_GROUP or TypeConstants.TAB
         * @param id The id of the widget
         * @returns the id of the ancestor
         * @private
         */
        _findAncestorId: function (ancestorType, id) {
            var guardians, ancestorId = id;

            while ((ancestorId !== null) && (this.get(ancestorId, "type") !== ancestorType)) {
                if (this._root === this.manager.getById(ancestorId)) {
                    ancestorId = null;
                    break;
                } else if (this.get(ancestorId, "type") === TypeConstants.GALLERY_POPUP) {
                    guardians = this.manager.getByProperty("galleryPopupId", ancestorId);
                    if (guardians.length === 0) {
                        this.actionService.getAllActions().forEach(function (action) {
                            if (action.popupId && (action.popupId === ancestorId)) {
                                this.manager.getByProperty("actionId", action.id).forEach(function (peerNode) {
                                    guardians.push(peerNode);
                                });
                            }
                        }.bind(this));

                        if (guardians.length === 0) {
                            ancestorId = null;
                            break;
                        }
                    }

                    if (guardians.length === 1) {
                        ancestorId = this.getParentId(guardians[0].getId());
                    } else {
                        // TODO: Filter QAB children, and set the ancestorId as the global tab group
                        ancestorId = this.getParentId(guardians[0].getId()); // Currently just picks the first parent
                        continue;
                    }
                } else if (this.get(ancestorId, "type") === TypeConstants.POPUP_LIST) {
                    ancestorId = this._findAncestorIdForPopupList(ancestorId);
                } else {
                    ancestorId = this.getParentId(ancestorId);
                }
            }

            return ancestorId;
        },

        /**
         * Returns the correct ancestorId for the PopupList. Popuplist can be opened directly from a toolstrip
         * control and even from QAB. When it is opened from QAB, we need to find its corresponding widget that will
         * have a parent within the toolstrip since we need find the correct TAB_GROUP that the widget resides and
         * ultimately use its corresponding QA Group to add it in QAB.
         * @param {String} id - id of the widget
         * @returns {*}
         * @private
         */
        _findAncestorIdForPopupList: function (id) {
            var ancestorId, ancestorWidget = registry.byId(id), qabId, qabWidget;
            if (ancestorWidget && ancestorWidget.domNode && ancestorWidget.domNode.parentElement
                && ancestorWidget.domNode.parentElement.getAttribute("dijitpopupparent")) {
                ancestorId = ancestorWidget.domNode.parentElement.getAttribute("dijitpopupparent");
            } else {
                ancestorId = null;
            }

            if (ancestorId) {
                // Check if this ancestorId belong to a widget in QAB
                qabId = this._toolstrip.get("QABId");

                ancestorWidget = registry.byId(ancestorId);
                qabWidget = registry.byId(qabId);

                if (qabWidget.domNode && ancestorWidget.domNode && qabWidget.domNode.contains(ancestorWidget.domNode)) {
                    // Find the corresponding widget to the QAB widget who has a parent within the toolstrip.
                    if (ancestorWidget.toolstripControlId) {
                        ancestorId = ancestorWidget.toolstripControlId;
                    }
                }
            }

            return ancestorId;
        },

        _createCorrespondingComponentInQAB: function (id, insertAsFirst, index) {
            var ancestorTabGroupId = this._findAncestorId(TypeConstants.TAB_GROUP, id);

            if (ancestorTabGroupId === null) {
                return;
            }

            var parentId = this.get(ancestorTabGroupId, "QAGroupId");
            var actionId = this.get(id, "actionId");
            var tag = this.get(id, "tag") + "_qab";
            var qabControlsWithTheSameAction = [];

            this.manager.getByType(TypeConstants.QA_GROUP).forEach(function (qaGroupPeerNode) {
                qabControlsWithTheSameAction = qabControlsWithTheSameAction.concat(this.manager.getByProperty("actionId", actionId, qaGroupPeerNode));
            }, this);

            if (qabControlsWithTheSameAction.length > 0) {
                var motwGlobalQaGroup = this.manager.getByProperty("tag", "motwToolstrip.globalTabGroup_qaGroup")[0];
                
                // If the new control is in the same group as an existing control, don't add it again
                    // Only potential consideration is whether to promote to Split-like widget.
                // If the existing item is from the global context, then we don't want to add a contextual one also
                var isNewQabControlInTheSameQAGroupAsAnyExistingControls = qabControlsWithTheSameAction.some(function (control) {
                    return ((parentId === qabControlsWithTheSameAction[0].getParent().getId()) || (qabControlsWithTheSameAction[0].getParent().getId() === motwGlobalQaGroup.getId()));
                });

                if (isNewQabControlInTheSameQAGroupAsAnyExistingControls) {
                    return id;
                }
            
                // If we are in MATLAB Online, and if the new item is from the global context then we
                // want to destroy the existing item and replace it with the global item.
                var controlWidget = registry.byId(qabControlsWithTheSameAction[0].getId());
                
                var isNewQabControlFromMotwGlobalQaGroup = (this.manager.getById(parentId) === motwGlobalQaGroup);
                
                var shouldExistingQabControlBeReplacedByGlobalControl = (motwGlobalQaGroup != undefined) && isNewQabControlFromMotwGlobalQaGroup;
                    
                if (shouldExistingQabControlBeReplacedByGlobalControl) {
                    // Destroy the existing QAB item and promote the new item to be global
                    this.destroy(qabControlsWithTheSameAction[0].getId());
                }
            }
            var qaGroup = registry.byId(parentId);

            // Creating a new QAB components results in resize calls. Set the flag on toolstrip to avoid doing any resize
            // operations such as collapsing the QAB widget within the overflow popup during these resize calls. We do
            // this because the QAB widget is placed as the first child or at a specified index later below which again
            // results in resize calls. However, we do want to do resize if this is the first QAB item that is getting
            // added to QAGroup because the successive move operations does not cause a toolstrip resize call.
            if (qaGroup.getChildren().length > 0) {
                this._toolstrip.setResizeFlag(false);
            }

            var qabid = this.create({
                type: ComponentsEligibleForQAB[this.get(id, "type")],
                tag: tag,
                actionId: actionId,
                parentId: parentId
            });

            this._toolstrip.setResizeFlag(true);

            if (typeof index === "number") {
                this.move(qabid, parentId, index);
            } else if (insertAsFirst) {
                this.move(qabid, parentId, 0);
            }

            this._postProcessingAfterQABWidgetCreation(qabid, id);
                
            return qabid;
        },

        /**
         * Creates corresponding component in QAB. This function is only supported when the QABAuthoringMode is manual.
         * @param {String} id  - id of the widget to be added in QAB
         * @param {String} QAGroupId - id of the QA Group in which the QAB widget would be added as the child
         * @param {Number} index - index at which the QAB widget needs to be added. Defaults to 0.
         * @instance
         * @returns {String} - id of the newly created QAB widget
         */
        createCorrespondingComponentInQAB: function (id, QAGroupId, index) {
            if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                Log.error("createCorrespondingComponentInQAB function is only supported in 'manual' QABAuthoringMode");
            } else {
                var tag = this.get(id, "tag") + "_qab";
                var actionId = this.get(id, "actionId");
                var qabid = this.create({
                    type: ComponentsEligibleForQAB[this.get(id, "type")],
                    tag: tag,
                    actionId: actionId,
                    parentId: QAGroupId
                });

                if (typeof index === "number") {
                    this.move(qabid, QAGroupId, index);
                } else {
                    this.move(qabid, QAGroupId, 0); // insert as first
                }

                this._postProcessingAfterQABWidgetCreation(qabid, id);

                var widget = registry.byId(id);
                if (!widget.get("isInQAB")) {
                    widget.set("isInQAB", true);
                }

                return qabid;
            }
        },

        _postProcessingAfterQABWidgetCreation: function (qabid, id) {
            var qabWidget = registry.byId(qabid);
            qabWidget.toolstripControlId = id;

            if (this.get(id, "type") === TypeConstants.LIST_ITEM_WITH_POPUP) {
                qabWidget.hasListItemWithPopupAction = true;
            }

            this._addEventCallbackToDynamicMenuWidgets(id, qabid);

            this._setIconIfApplicableForListItemTypeWidgets(qabid, id);

            this._hookupRemoveFromQABListener(qabid, id);
        },

        _setIconIfApplicableForListItemTypeWidgets: function (qabId, id) {
            // check if the icon is present on the QAB widget recently added
            var qabWidget = registry.byId(qabId);
            var widget = registry.byId(id);

            if (qabWidget && qabWidget.get("icon") === "" && qabWidget.get("quickAccessIcon") === "") {
                // Icon was not set, grab it from the parent.
                var parent = widget.getParent();
                while (parent && parent._invokingWidget && parent._invokingWidget.get("quickAccessIcon") === "") {
                    parent = parent._invokingWidget.getParent();
                }

                if (parent && parent._invokingWidget && parent._invokingWidget.get("quickAccessIcon") !== "") {
                    // Found quickAccessIcon of a parent, set it as the icon for the qabWidget
                    qabWidget.set("icon", parent._invokingWidget.get("quickAccessIcon"));
                }
            }
        },

        /**
         * Adds event callback to the dropDownPerformed event of the QAB widget.
         *
         * @param {String} id - id of the widget
         * @param {String} qabid - id of the correponding QAB widget
         * @private
         */
        _addEventCallbackToDynamicMenuWidgets: function (id, qabid) {
            var widget = registry.byId(id);
            if (widget && widget.get("hasDynamicMenu") === true) {
                widget.qabDropdownPerformedEventCallback =  this.addEventCallback(qabid, function (eventData) {
                    if (eventData.eventType === "dropDownPerformed") {
                        widget.dropdownPerformedQABId = qabid;
                        widget.emit("dropdownclick", {});
                    }
                });
            }
        },

        /**
         * Removes the callback on the QAB widget for dropDownPerformed event.
         *
         * @param {String} id - id of the widget
         * @private
         */
        _removeQABDropdownPerformedEventCallback: function (id) {
            var widget = registry.byId(id);
            if (widget && widget.qabDropdownPerformedEventCallback && widget.qabDropdownPerformedEventCallback.remove) {
                widget.qabDropdownPerformedEventCallback.remove();
                delete widget.qabDropdownPerformedEventCallback;
            }
        },

        _hookupAddToQABListener: function (id) {
            var actionId = this.get(id, "actionId");
            this.actionService._actionById(actionId).addEventListener("propertySet", function (e) {
                if (e.data.key === "isInQAB" && e.data.newValue === true) {
                    if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                        this._createCorrespondingComponentInQAB(id, true);
                    }
                }
            }, this);
        },

        _hookupRemoveFromQABListener: function (qabId, id) {
            var actionId = this.get(qabId, "actionId");
            this.actionService._actionById(actionId).addEventListener("propertySet", function (e) {
                if (e.data.key === "isInQAB" && e.data.newValue === false) {
                    if (this.exists(qabId)) {
                        this.destroy(qabId);

                        if (typeof id !== "string") {
                            // This qabWidget was added in the previous session. Find the corresponding widget
                            // within the toolstrip from the tag.
                            var qabWidget = registry.byId(qabId);
                            if (qabWidget) {
                                var qabWidgetTag = registry.byId(qabId).get("tag");
                                var tagOfCorrespondingControlInToolstrip = qabWidgetTag.replace("_qab", "");
                                id = this.tagToId(tagOfCorrespondingControlInToolstrip);
                            }
                        }

                        this._removeQABDropdownPerformedEventCallback(id);
                    }
                }
            }, this);
        },

        /**
         *
         * @param peerNode
         * @private
         */

        _postProcessing: function (peerNode) {
            var QABId, QAGroupId, favCategoryId, parentNode, parentQABId, tabGroupTag;

            if (Object.keys(ComponentsEligibleForQAB).indexOf(peerNode.getType()) > -1) {
                this._hookupAddToQABListener(peerNode.getId());
                if (this.get(peerNode.getId(), "isInQAB") === true) {
                    if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                        this._createCorrespondingComponentInQAB(peerNode.getId(), true);
                    }
                }
            }

            switch (peerNode.getType()) {
            case TypeConstants.TOOLSTRIP:
                QABId = this.create({
                    type: TypeConstants.QAB
                });
                this.set(peerNode.getId(), "QABId", QABId);
                break;
            case TypeConstants.TAB_GROUP:
                if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                    // Create corresponding QA Group for Tab Group
                    parentNode = peerNode.getParent();
                    if (parentNode.getType() === TypeConstants.TOOLSTRIP) {
                        parentQABId = parentNode.getProperty("QABId");
                    }
                    tabGroupTag = this.get(peerNode.getId(), "tag");

                    QAGroupId = this.create({
                        type: TypeConstants.QA_GROUP,
                        parentId: parentQABId,
                        tag: tabGroupTag ? tabGroupTag + "_qaGroup" : ""
                    });
                    this.set(peerNode.getId(), "QAGroupId", QAGroupId);
                }
                break;
            case TypeConstants.GALLERY_PANEL:
            case TypeConstants.GALLERY_POPUP:
                favCategoryId = this.create({
                    type: TypeConstants.GALLERY_FAVORITE_CATEGORY,
                    dndEnabled: true
                });
                this.set(peerNode.getId(), "favCategoryId", favCategoryId);
                this.addEventCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "galleryStateChanged") {
                        peerNode.getChildren().forEach(function (child) {
                            child.setProperty("qabEligible", peerNode.getProperty("qabEligible") || false);
                        }.bind(this));
                    }
                }, this);
                this.addPropertySetCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "qabEligible") {
                        peerNode.getChildren().forEach(function (child) {
                            child.setProperty("qabEligible", peerNode.getProperty("qabEligible") || false);
                        }.bind(this));
                    }
                }, this);
                break;
            case TypeConstants.GALLERY:
                // Initialize the current column count to be the same as the maxColumnCount
                this.set(peerNode.getId(), "currentColumnCount", this.get(peerNode.getId(), "maxColumnCount"));
                break;
            case TypeConstants.GALLERY_CATEGORY:
                this.addEventCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "categoryMoved" && ["Top", "Bottom", "Drop"].indexOf(data.action) > -1) {
                        this._widgetByPeerNode(peerNode.getParent())._moving = true;
                        switch (data.action) {
                            case "Top":
                                this.manager.move(peerNode, peerNode.getParent(), 0);
                                break;
                            case "Bottom":
                                this.manager.move(peerNode, peerNode.getParent(), peerNode.getParent().getNumberOfChildren() - 1);
                                break;
                            case "Drop":
                                this.manager.move(peerNode, peerNode.getParent(), data.newIndex);
                                break;
                        }

                        if (data.originator && data.originator.source === "UI") {
                            // Firing the galleryStateChanged as the user moved category
                            this._fireGalleryStateChanged(peerNode, true);
                        } else {
                            this._fireGalleryStateChanged(peerNode, false);
                        }
                    }
                    if (data.eventType === "itemMoved") {
                        this.manager.move(this.manager.getById(data.itemId), peerNode, data.newIndex);

                        if (data.originator && data.originator.source === "UI") {
                            // Firing the galleryStateChanged as the user moved item
                            this._fireGalleryStateChanged(peerNode,  true);
                        } else {
                            this._fireGalleryStateChanged(peerNode, false);
                        }
                    }
                }, this);
                this.addPropertySetCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "qabEligible") {
                        peerNode.getChildren().forEach(function (child) {
                            child.setProperty("qabEligible", peerNode.getProperty("qabEligible") || false);
                        }.bind(this));
                    }
                }, this);
                break;
            case TypeConstants.QA_GROUP:
                this.addEventCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "itemMoved") {
                        this.manager.move(this.manager.getById(data.itemId), peerNode, data.newIndex);
                    }
                }, this);
                break;
            case TypeConstants.GALLERY_FAVORITE_CATEGORY:
                this.addEventCallback(peerNode.getId(), function (data) {
                    if (data.eventType === "itemMoved") {
                        this.manager.move(this.manager.getById(data.itemId), peerNode, data.newIndex);

                        if (data.originator && data.originator.source === "UI") {
                            // Firing the galleryStateChanged as the user moved item
                            this._fireGalleryStateChanged(peerNode, true);
                        } else {
                            this._fireGalleryStateChanged(peerNode, false);
                        }
                    }
                }, this);
                break;
            case TypeConstants.GALLERY_ITEM:
            case TypeConstants.TOGGLE_GALLERY_ITEM:
                if (this.get(peerNode.getId(), "isFavorite")) {
                    peerNode.setProperty("isFavorite", false); // Force addGalleryItemToFavorites to work
                    this.addGalleryItemToFavorites(peerNode.getProperty("tag"), false);
                }
                this.addEventCallback(peerNode.getId(), function (data) {
                    var tag, isFavorite;

                    if (data.eventType === "favButtonPushed") {
                        tag = peerNode.getProperty("tag");
                        isFavorite = peerNode.getProperty("isFavorite");

                        if (isFavorite) {
                            this.removeGalleryItemFromFavorites(tag, true);
                        } else {
                            this.addGalleryItemToFavorites(tag, true);
                        }
                    }
                }, this);
                break;
            }
        },

        _fireGalleryStateChanged: function (peerNode, fireEventOnGalleryPopupOrPanel) {
            var galleryCategory = this.widgetById(peerNode.getId());
            if (galleryCategory && galleryCategory.galleryStateChanged) {
                galleryCategory.galleryStateChanged(fireEventOnGalleryPopupOrPanel);
            }
        },

        /**
         * Function to add a GalleryItem to the favorites category. This method should be called
         * only after the GalleryItem has been added to a gallery. Calling this method on orphaned 
         * GalleryItems is not supported. Orphaned GalleryItems are the ones that are not part of
         * any gallery.
         * @example <caption>Example usage of addGalleryItemToFavorites.</caption>
         *  var tb = UIBuilderFactory.getInstance();
         *  var galleryPopupId = uiBuilder.create({
         *      type: TypeConstants.GALLERY_POPUP,
         *      tag: "myGallery"
         *      });
         *  var galleryCategoryId = uiBuilder.create({
                type: TypeConstants.GALLERY_CATEGORY,
                parentId: galleryPopupId,
                title: "EXAMPLE CATEGORY",
                tag: "myGalleryCategory"
            });
         *  var galleryItemId = uiBuilder.create({
         *      type: TypeConstants.GALLERY_ITEM,
         *      parentId: galleryCategoryId,
         *      tag: "myGalleryItem",
         *      text: "Some Text",
         *      description: "I do something awesome!"
         *      });
         *  // favorite the gallery item
         *  tb.addGalleryItemToFavorites("myGalleryItem", false);
         * @param {string} tag The tag of gallery item to be favorited.
         * @param {boolean} userInteraction True if this was done as a part of user interaction. A 'galleryStateChanged' event is
         * fired whenever a gallery item is favorited as a part of user interaction. Defaults to false.
         * @instance
         * @returns undefined
         */

        addGalleryItemToFavorites: function (tag, userInteraction) {
            var actionId, isFavorite, favCategoryId, parentCategory, peerNode, favCategoryItemId;

            peerNode = this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.GALLERY_PANEL_ROOT)) ||
                this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.GALLERY_POPUP_ROOT)) ||
                this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.ORPHAN_ROOT));

            if (!peerNode) {
                return;
            }

            parentCategory = peerNode.getParent();
            actionId = peerNode.getProperty("actionId");

            isFavorite = peerNode.getProperty("isFavorite");

            if (isFavorite) {
                return;
            }

            favCategoryId = parentCategory.getParent().getProperty("favCategoryId");
            // g1698140: Pass in the fact that this widget is being created as a favorite
            favCategoryItemId = this.create({
                type: peerNode.getType(),
                tag: peerNode.getProperty("tag")  + "_favorite",
                actionId: actionId,
                parentId: favCategoryId,
                removable: peerNode.getProperty("removable"),
                iconOverride: peerNode.getProperty("iconOverride"),
                textOverride: peerNode.getProperty("textOverride"),
                descriptionOverride: peerNode.getProperty("descriptionOverride"),
                originator: userInteraction ? {source: "addGalleryItemToFavorites"} : {source: "addGalleryItemToFavoritesProgrammatic"}
            });

            this._setupNotInActionPropertiesSyncCallback(peerNode, favCategoryItemId);

            this.set(favCategoryItemId, "isFavorite", true);
            peerNode.setProperty("isFavorite", true);
        },

        /**
        * Sets up the propertySetCallback for the override properites on the favorite item and stores the remove function in map
        *
        * @param peerNode
        * @instance
        * @returns undefined
        */
        _setupNotInActionPropertiesSyncCallback: function (peerNode, favCategoryItemId) {
            // update the overrides on the favorite item
            var notInActionPropertiesSync = ["iconOverride", "textOverride", "descriptionOverride"];

            var wrapperCallback = function (data) {
                var data = data.data;

                if (notInActionPropertiesSync.includes(data.key)) {
                    this.set(favCategoryItemId, data.key, data.newValue);
                }
            };

            this._notInActionPropertiesSyncCallbackMap[peerNode.getId()] = wrapperCallback;
            peerNode.addEventListener("propertySet", wrapperCallback, this);
        },

        /**
        * Function to remove a GalleryItem from the favorites category. Calling this method on orphaned 
        * GalleryItems is not supported. Orphaned GalleryItems are the ones that are not part of
        * any gallery.
        * @param {string} tag The tag of gallery item to be removed from favorites.
        * @param {boolean} userInteraction True if this was done as a part of user interaction. A 'galleryStateChanged'
        * is fired whenever a gallery item is removed from favorites as a part of user interaction. Defaults to false.
        * @instance
        * @returns undefined
         */

        removeGalleryItemFromFavorites: function (tag, userInteraction) {
            var isFavorite, peerNode, favItemNode, peerNodeId, originator;

            tag = tag.replace("_favorite", "");

            peerNode = this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.GALLERY_PANEL_ROOT)) ||
                this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.GALLERY_POPUP_ROOT)) ||
                this._peerNodeByTag(tag, this._getRootNodeByType(TypeConstants.ORPHAN_ROOT));

            if (!peerNode) {
                return;
            }

            isFavorite = peerNode.getProperty("isFavorite");

            if (!isFavorite) {
                return;
            }

            favItemNode = this._peerNodeByTag(tag + "_favorite");

            if (favItemNode) {
              originator = userInteraction ? {source: "removeGalleryItemFromFavorites"} : {source: "removeGalleryItemFromFavoritesProgrammatic"};
              this.destroy(favItemNode.getId(), originator);
            }

            peerNodeId = peerNode.getId();
            peerNode.removeEventListener("propertySet", this._notInActionPropertiesSyncCallbackMap[peerNodeId], this);
            delete this._notInActionPropertiesSyncCallbackMap[peerNodeId];

            peerNode.setProperty("isFavorite", false);
        },

        /**
         * Adds an already created but orphan component to the given parent. If an index is not provided, the child is added as the last one.
         *
         * @example
         * uiBuilder.add(mySectionId, myTabId);
         * uiBuilder.add(mySectionId, myTabId, 2);
         * @param {string} childId  The id of the child component
         * @param {string} parentId The id of the parent component
         * @param {Number} index    Position of the child component with in the parent component
         * @instance
         * @returns undefined
         */
        add: function (childId, parentId, index, originator) {
            var childNode, parentNode, QABId, QAGroupId;

            if (!this._getRootNodeByType(TypeConstants.ORPHAN_ROOT).hasDescendant(childId)) {
                Log.assert(false, "The child being added should be an orphan!");
                return;
            }

            childNode = this.manager.getById(childId);
            parentNode = this.manager.getById(parentId);

            Log.assert(parentNode, "The parentId provided is not valid!");

            // If parent is footercell and if widget type is not one of them in ComponentsThatSupportGalleryFooter, do not create widget
            // For example, Text Area is not supported in Gallery Footer cell as TextArea spans the entire column
            if (parentNode.getType() === "FooterCell") {
                if (ComponentsSupportedByFooterCell.indexOf(childNode.getType()) === -1) {
                    Log.warn(childNode.getType() + " is not supported by Footer Cell");
                    return;
                }
            }

            // If the index is out of bounds, set it to the number of children.
            if (index < 0 || index > parentNode.getChildren().length) {
                index = parentNode.getChildren().length;
            }

            this.manager.move(childNode, parentNode, index, originator);

            if (childNode.getProperty('_wasFavorite')) {
                this.addGalleryItemToFavorites(childNode.getProperty("tag"));
            }

            if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                // If the child being added is a tab group then add the corresponding qa group to the qab as well
                if (this.get(childId, "type") === TypeConstants.TAB_GROUP) {
                    if (this.get(parentId, "type") === TypeConstants.TOOLSTRIP) {
                        QAGroupId = this.get(childId, "QAGroupId");
                        QABId = this.get(parentId, "QABId");
                        this.add(QAGroupId, QABId, this.manager.getById(QABId).getNumberOfChildren() - index, originator);
                    }
                }
            }

            // g1735045: When any control is added after toolstrip is loaded, call resize method to ensure that the
            // toolstrip contents are displayed correctly based on the toolstrip width.
            this._resizeToolstrip();

        },

        /**
         * Makes the peer node corresponding to the given id an orphan.
         *
         * @example
         * uiBuilder.remove(mySectionId);
         * @param {string} id The id of the peer node that you want to make an orphan.
         * @instance
         */
        remove: function (id, originator) {
            var peerNode;

            if (!this.exists(id)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            peerNode = this.manager.getById(id);

            // g1698140: If the peerNode is a GalleryItem, then we need to remove the favorite GalleryItem as well
            if ((peerNode.getProperty("type") === "GalleryItem" || peerNode.getProperty("type") === "ToggleGalleryItem")
                && peerNode.getParent() && peerNode.getParent()._type !== "GalleryFavoriteCategory") {
                peerNode.setProperty('_wasFavorite', peerNode.getProperty("isFavorite"));
                this.removeGalleryItemFromFavorites(peerNode.getProperty("tag"));
            }

            this.manager.move(peerNode, this._getRootNodeByType(TypeConstants.ORPHAN_ROOT), undefined, originator);

            if (this._qabAuthoringMode === QABAuthoringModeConstants.DEFAULT) {
                // If the component being removed is a tab group then also remove the corresponding QA Group.
                if (this.get(id, "type") === TypeConstants.TAB_GROUP) {
                    this.remove(this.get(id, "QAGroupId"));
                }
            }

            // Resize toolstrip since removing a component will free some real estate within the toolstrip.
            this._resizeToolstrip();
        },

        /**
         * Destroys the peer node corresponding to the given id along with it's children.
         * @param {string} id The id of the peer node that you want to destroy.
         * @instance
         * @returns undefined
         */
        destroy: function (id, originator) {
            var peerNode, actionId;

            if (!this.exists(id)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            peerNode = this.manager.getById(id);
            
            // g1698140: If the peerNode is a GalleryItem, then we need to remove the favorite GalleryItem as well
            if ((peerNode._type === "GalleryItem" || peerNode._type === "ToggleGalleryItem")
                && peerNode.getParent() && peerNode.getParent()._type !== "GalleryFavoriteCategory") {
                var favItemTag = peerNode._properties.tag + "_favorite";

                if (this._peerNodeByTag(favItemTag)) {
                    // don't call the remove on favorites if it doesn't exist. see geck g2124457
                    this.removeGalleryItemFromFavorites(peerNode._properties.tag);
                }
            }
            
            this._destroyAutoGeneratedActionIdsRecursive(peerNode);
            
            peerNode.destroy(originator);

            // Resize toolstrip since destroying a component will free some real estate within the toolstrip.
            this._resizeToolstrip();
        },
        
        _destroyAutoGeneratedActionIdsRecursive: function (peerNode) {
            var children = peerNode.getChildren();
            if (children.length > 0) {
                for (var i = 0; i < children.length; i++) {
                    this._destroyAutoGeneratedActionIdsRecursive(children[i]);
                }
            }
            
            var actionId = peerNode.getProperty("actionId");
            if (this._autoGeneratedActions[actionId]) {
                var actionNodes = this.manager.getByProperty("actionId", actionId);
                if ((actionNodes.length === 1) && (actionNodes[0].getId() === peerNode.getId())) {
                    this.actionService.removeAction(actionId);
                    delete this._autoGeneratedActions[actionId];
                }
            }
        },

        /**
         * Returns the value of the given property from the peer node corresponding to the given
         * id.
         *
         * @example
         * uiBuilder.get(myButtonId, "text");
         *
         * @param {string} id The id of the model object whose property's value needs to be fetched.
         * @param {string} property The property whose value needs to be fetched.
         * @instance
         * @returns  Returns the value of the property asked for
         */
        get: function (id, property) {
            var actionId, peerNode, result;

            if (!this.exists(id)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            peerNode = this.manager.getById(id);

            if (property === "type") {
                result = peerNode.getType();
            } else if (ComponentPropertiesHash[peerNode.getType()][property].notInAction) {
                Log.assert(peerNode.hasProperty(property), "This is not a valid property!");
                result = peerNode.getProperty(property);
            } else if (this._supportsAction(peerNode.getType())) {
                actionId = peerNode.getProperty("actionId");
                Log.assert(this.actionService.actionHasProperty(actionId, property),
                    "This is not a valid property!");
                result = this.actionService.getAction(actionId)[property];
            } else {
                Log.assert(false, "This is not a valid property!");
            }

            return result;
        },

        /**
         *
         * @param id
         * @private
         */

        getChildrenIds: function (id) {
            var peerNode;

            if (!this.exists(id)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            peerNode = this.manager.getById(id);

            return peerNode.getChildren().map(function (child) {
                return child.getId();
            });
        },
        /**
         *  Returns true if a component with this id was created already and has not been destroyed yet
         *
         * @example
         *  uiBuilder.exists(mySectionId);
         *  @param {string} id of widget
         *  @instance
         *  @returns  {boolean} True, if if a component is found; false otherwise
         */


        exists: function (id) {
            return this.manager.hasById(id);
        },

        /**
         *  Returns the index of a component with this id within it's parent. If this component is an orphan, then -1 is returned. Also, for components that cannot be parented like popup list etc. -1 is returned.
         *
         *  @example
         *  var index = uiBuilder.indexOf(mySectionId);
         *  @param {string} id of the component whose index you want to fetch.
         *  @instance
         *  @returns {Number} 0,1,2,... if a component is parented; -1 otherwise
         */


        indexOf: function (id) {
            var index = -1, node;

            node = this.manager.getById(id);

            if (this.getParentId(id)) {
                index = node.getParent().getChildIndex(node);
            }

            return index;
        },
        /**
         *
         * @param id
         * @returns {boolean}
         * @private
         */

        isAnOrphan: function (id) {
            return this._getRootNodeByType(TypeConstants.ORPHAN_ROOT).hasDescendant(id);
        },
        /**
         *
         * @param id
         * @private
         */

        getParentId: function (id) {
            var result = null, node;

            if (!this.exists(id)) {
                Log.assert(false, "A component with the given id does not exist!");
                return result;
            }

            node = this.manager.getById(id);

            // If the node is not a direct child of orphan root and is not a special type which does not have a parent
            // like popup list etc. then return the parentId otherwise return null
            if (!this._getRootNodeByType(TypeConstants.ORPHAN_ROOT).hasChild(id) && ComponentsWithNoParent.indexOf(node.getType()) === -1) {
                return node.getParent().getId();
            }

            return result;
        },

        /**
         * Sets the value of the given property to the given new value for the peer node
         * corresponding to the given id.
         *
         * @example
         * uiBuilder.set(myButtonId, "text", "New Text");
         *
         * @param {string} id The id of the model object whose property's value needs to be set.
         * @param {string} property The property whose value needs to be set.
         * @param value The new value that needs to be set.
         * @param {string} originator The source of the set. It is set to source: "UI" when it comes from the widget watch.
         * @instance
         * @returns undefined
         */
        set: function (id, property, value, originator) {
            var actionId, peerNode, componentProps, actionProps = {};
            if (!this.exists(id)) {
                Log.assert(false, "The node does not exist!");
                return;
            }
            peerNode = this.manager.getById(id);

            componentProps = ComponentPropertiesHash[peerNode.getType()];

            if (!componentProps[property] || !this._isAValidValue(value, componentProps[property], property)) {
                return;
            }

            if (componentProps[property].notInAction) {
                peerNode.setProperty(property, value, originator);
            } else if (this._supportsAction(peerNode.getType())) {
                actionId = peerNode.getProperty("actionId");
                actionProps[property] = value;
                this.actionService.updateAction(actionId, actionProps, originator);
            } else {
                Log.assert(false, "This is not a valid property!");
            }

            // Resize toolstrip when text property is set on the component as it could potentially increase its width.
            if (property === "text") {
                this._resizeToolstrip();
            }
        },

        /**
         * Moves a peer node to a new parent and/or index.
         * @param {string} childId The id of the peer node that needs to be moved.
         * @param {string} newParentId The id of the new parent of the peer node being moved.
         * @param {number} newIndex The new index starting from 0.
         * @private
         */
        move: function (childId, newParentId, newIndex, originator) {
            var childNode, newParentNode;

            childNode = this.manager.getById(childId);
            Log.assert(childNode, "The node being moved does not exist!");

            newParentNode = this.manager.getById(newParentId);
            Log.assert(newParentNode, "The new parent node does not exist!");

            // If the index is out of bounds, set it to the number of children.
            if (newIndex < 0 || newIndex > newParentNode.getChildren().length) {
                newIndex = newParentNode.getChildren().length;
            }

            this.manager.move(childNode, newParentNode, newIndex, originator);
        },
        /**
         *
         * @param id
         * @private
         */

        getAllProperties: function (id) {
            var peerNode, actionId, result, actionProperties;
            peerNode = this.manager.getById(id);
            result = peerNode.getProperties();
            if (this._supportsAction(peerNode.getType())) {
                actionId = peerNode.getProperty("actionId");
                if (actionId) {
                    actionProperties = this.actionService.getAction(actionId);
                    // There could be a case where actionId exits, but the action is not created yet
                    if (actionProperties) {
                        Object.keys(actionProperties).forEach(lang.hitch(this, function (key) {
                            if (!ComponentPropertiesHash[peerNode.getType()][key]) {
                                delete actionProperties[key];
                            }
                        }));
                    }

                    result = lang.mixin(result, actionProperties);
                }
            }
            return result;
        },

        /**
         * Resize the toolstrip.
         * @private
         */
        _resizeToolstrip: function () {
            if (!this._toolstrip) {
                if (this._toolstripRoot) {
                    var toolstripRootChildMatch = this.manager.getByProperty("type", TypeConstants.TOOLSTRIP, this._toolstripRoot);
                    if (toolstripRootChildMatch.length > 0) {
                        var toolstrip = toolstripRootChildMatch[0];
                        if (toolstrip && toolstrip.getId()) {
                            this._toolstrip = registry.byId(toolstrip.getId());
                        }
                    }
                }
            }

            if (this._toolstrip && this._toolstrip.resizeToolstripWithDelay) {
                this._toolstrip.resizeToolstripWithDelay();
            }
        },

        _supportBase64URL: function (properties) {
            var base64SupportedProperties = Object.keys(IconPropertiesSupportingBase64), value;
            base64SupportedProperties.forEach(lang.hitch(this, function (property) {
                if (property in properties) {
                    value = properties[property];
                    if (typeof value === "string" && value.indexOf("url(") === 0) {
                        properties[property] = this._stripURL(value);
                    }
                }
            }));
        },

        _stripURL: function (value) {
            var temp = value.slice(4);
            return temp.slice(0, temp.length - 1);

        },

        /**
         * Check the properties against the component properties hash to make sure that the required
         * properties are present and the other missing properties are given default values.
         * @param properties
         * @private
         */
        _verifyProperties: function (properties) {
            var verifiedProperties = {}, componentProperties, galleryPopup,
                result = {viewNodeProps: {}, actionNodeProps: {}};

            Log.assert(properties.type, "Type needs to be provided for all components!");

            componentProperties = ComponentPropertiesHash[properties.type];

            result.type = properties.type;

            this._convertPopupTagToId(properties);

            Object.keys(componentProperties).forEach(function (key) {
                if (this._isAValidValue(properties[key], componentProperties[key], key)) {
                    verifiedProperties[key] = properties[key];
                } else {
                    if (componentProperties[key].required) {
                        Log.error(key + " needs to be provided for component of type " + properties.type);
                    } else {
                        verifiedProperties[key] = componentProperties[key].defaultValue;
                    }
                }
            }, this);

            Object.keys(verifiedProperties).forEach(function (key) {
                if (componentProperties[key].notInAction) {
                    result.viewNodeProps[key] = verifiedProperties[key];
                } else {
                    result.actionNodeProps[key] = verifiedProperties[key];
                }
            });

            if (result.type === TypeConstants.GALLERY_POPUP) {
                // Assign the correct default width for the gallery item depending on the item row count and the
                // item's text line count.
                if (!result.viewNodeProps.galleryItemWidth) {
                    result.viewNodeProps.galleryItemWidth = DefaultGalleryItemWidths[result.viewNodeProps.galleryItemRowCount][result.viewNodeProps.galleryItemTextLineCount];
                }
            } else if (result.type === TypeConstants.GALLERY) {
                // Assign the gallery item details to the Gallery based on those specified in the Gallery Popup
                galleryPopup = this.manager.getById(result.viewNodeProps.galleryPopupId);
                result.viewNodeProps.galleryItemWidth = galleryPopup.getProperty("galleryItemWidth");
                result.viewNodeProps.galleryItemRowCount = galleryPopup.getProperty("galleryItemRowCount");
                result.viewNodeProps.galleryItemTextLineCount = galleryPopup.getProperty("galleryItemTextLineCount");
                result.viewNodeProps.scrollToSelection = galleryPopup.getProperty("scrollToSelection");
                result.viewNodeProps.qabEligible = galleryPopup.getProperty("qabEligible");
            }

            return result;
        },
        /**
         *
         * @param props
         * @private
         */

        _convertPopupTagToId: function (props) {
            var idProp, propsToSet = {}, tagProps = {
                popupTag: "popupId",
                galleryPopupTag: "galleryPopupId"
            };

            Object.keys(tagProps).forEach(function (key) {
                var popupId;
                idProp = tagProps[key];
                if (props[key]) {
                    popupId = this.tagToId(props[key]);
                    if (popupId) {
                        props[idProp] = popupId;
                    }
                    propsToSet[idProp] = props[idProp];
                    if (propsToSet[idProp]) {
                        this.actionService.updateAction(props.id, propsToSet);
                    }
                }
            }, this);
        },
        /**
         *  Check for valid values that can be set from create, set and verifyproperties functions
         * @param value
         * @param propertyObject
         * @param property
         * @returns {boolean}
         * @private
         */

        _isAValidValue: function (value, propertyObject, property) {
            var convertToLower, isValid = true, type;
            //Cannot pass undefined or null values to properties
            if (value === undefined || value === null) {
                isValid = false;
                // As Typeof Array is object, Branching  here to check if the value is an array or not
            } else if (propertyObject.type === "Array") {
                // If the value is not of type array, it is not a valid value
                if (!Array.isArray(value)) {

                    Log.warn(property + " property default type is " + propertyObject.type + " but you have passed " + typeof value);
                    isValid = false;
                } //For all other data types like Number, string, Boolean (also Object, currently we do not have any objects)
            } else {
                //TODO: rename in component properties hash and remove toLowerCase() method
                convertToLower = propertyObject.type.toLowerCase();
                //If the data type of value is not equal to propertyObject data type
                if ((typeof value !== convertToLower)) {
                    isValid = false;
                    if (Array.isArray(value)) {
                        type = "Array";
                    } else {
                        type = typeof value;
                    }
                    Log.warn(property + " property default type is " + propertyObject.type + " but you have passed " + type);
                }
            }
            return isValid;
        },

        /**
         * Makes the given tab the selected tab in it's tab group
         *
         * @example uiBuilder.selectTab("myTabTag")
         * @param {string} tabTag  The tag of any tab in it's tab group
         * @returns undefined
         * @instance
         */
        selectTab: function (tabTag) {

            // Check in map first if tabTag is present or not
            var tabNode, tabGroupNode;
            if (this.tagToPeerNodeIdMap[tabTag]) {
                tabNode = this.manager.getById(this.tagToPeerNodeIdMap[tabTag]);
                tabGroupNode = tabNode.getParent();
                this._widgetByPeerNode(tabGroupNode).set("selectedTab", tabNode.getId());
            }

        },
        /**
         * Selects the first tab in the given tab group
         *
         * @example
         * uiBuilder.selectFirstTabInTabGroup("myTabGroupTag")
         * @param {string} tabGroupTag The tag of the TabGroup
         * @instance
         * @returns undefined
         */

        selectFirstTabInTabGroup: function (tabGroupTag) {
            // Check in map first if tabTag is present or not
            var tabGroupNode;
            if (this.tagToPeerNodeIdMap[tabGroupTag]) {
                tabGroupNode = this.manager.getById(this.tagToPeerNodeIdMap[tabGroupTag]);

                if (tabGroupNode && tabGroupNode.getChildren().length > 0) {
                    this.set(this.getParentId(tabGroupNode.getId()), "selectedTab", "");
                    this.set(this.getParentId(tabGroupNode.getId()), "selectedTab", tabGroupNode.getChildren()[0].getId());
                }
            }
        },

        /**
         * Places the toolstrip widget's dom node at the given parent node
         * @example
         * uiBuilder.placeToolstripAt("myToolstripTag")
         *
         * @param {string} toolstripTag The tag of the toolstrip
         * @param {string} parent The id of the parent
         * @instance
         * @returns undefined
         */
        placeToolstripAt: function (toolstripTag, parent) {
            var toolstripWidget = this.widgetByTag(toolstripTag);
            toolstripWidget.placeAt(parent);
            toolstripWidget.startup();
        },

        /**
         * @param toolstripId
         * @param parent
         * @private
         */
        placeToolstripById: function (toolstripId, parent) {
            var toolstripWidget = this.widgetById(toolstripId);
            toolstripWidget.placeAt(parent);
            toolstripWidget.startup();
        },

        /**
         * Returns the widget corresponding to the tag.
         * @private
         */
        widgetByTag: function (tag) {
            return registry.byId(this.tagToId(tag));
        },
        /**
         *
         * @param id
         * @returns {*|HTMLElement}
         * @private
         */

        widgetById: function (id) {
            return registry.byId(id);
        },
        /**
         *
         * @param peerNode
         * @returns {*|HTMLElement}
         * @private
         */

        _widgetByPeerNode: function (peerNode) {
            return registry.byId(peerNode.getId().toString());
        },
        /**
         *
         * @param tag
         * @param seedNode
         * @private
         */

        _peerNodeByTag: function (tag, seedNode) {
            seedNode = seedNode || this._getRootNodeByType(TypeConstants.ROOT);
            return this.manager.getByProperty("tag", tag, seedNode)[0];
        },

        /**
         * Opens/closes the popup of the widget corresponding to the given id.
         * This is used to trigger the opening of the button drop down after it has been populated
         * dynamically.
         * @private
         */
        showPopup: function (id, popupId) {
            var widgetWithinToolstripTab, parentWidget = registry.byId(id.toString());

            if (parentWidget.dropdownPerformedQABId) { // dropdown menu opened from the widget within QAB
                widgetWithinToolstripTab = parentWidget;
                parentWidget = registry.byId(parentWidget.dropdownPerformedQABId);
            }

            // Verify that the popup's parent is in the DOM before showing it.
            if (parentWidget && dom.byId(domAttr.get(parentWidget.domNode, "widgetid"))) {

                if (listOfNewWidgets.indexOf(parentWidget.type) > -1) {
                    if (popupId) {
                        parentWidget.set("menuId", popupId);
                    }
                    parentWidget.showMenu();
                } else {
                    parentWidget.set("popupId", popupId);
                    if (ToolstripUtils.isAListItemWithPopup(parentWidget.get("type"))) {
                        //TODO: This should be revisited when we upgrade to new version of dojo.
                        parentWidget.getParent().hover_timer =
                            parentWidget.getParent().defer(function () {
                                parentWidget.getParent()._openItemPopup(parentWidget);
                            }, parentWidget.getParent().popupDelay);
                    } else {
                        parentWidget.toggleDropDown();
                    }
                }

                if (parentWidget.get("hasDynamicMenu")) {
                    // Both QAB widget and widget within the toolstrip tab have the same menu. If the menu is opened
                    // from one of the two (lets call it the first widget) and without closing it, if the menu from the
                    // other widget (lets call it the second widget) is opened, invokingWidget gets unset from the
                    // onClose callback of the first widget's menu. Due to this, even though the menu of the second
                    // widget is already open, clicking again on the dropdown button of the second widget does not
                    // close the menu. Setting the invokingWidget will make sure that the menu gets closed for the
                    // second widget if its already open.
                    parentWidget.get("menu")._invokingWidget = parentWidget;
                }

                if (widgetWithinToolstripTab) {
                    // Clean up the dropdownPerformedQABId property to ensure dropdown menu opens as expected when
                    // the user clicks on the dropdown in the widget within toolstrip tab.
                    delete widgetWithinToolstripTab.dropdownPerformedQABId;
                }
            }
        },

        /**
         * Adds the event callback to the component whose id is supplied.
         *
         * @example
         * uiBuilder.addEventCallback(myButtonId, function (eventData) {
         *     console.log(eventData);
         * });
         * //Providing scope
         * uiBuilder.addEventCallback(myButtonId, function (eventData) {
         *     console.log(eventData);
         * }, someObj);
         *
         * @param id {string} The id of the component
         * @param callback {function} The function to be fired when any event is triggered.
         * @param scope {object} The scope in which the callback will run.
         * @instance
         * @returns {object} You should save this in a variable. This object has a method "remove" that can be used to clean up this callback.
         */
        addEventCallback: function (id, callback, scope) {
            var actionId, peerNode, wrapperCallback, actionNode, newCallback;

            Log.assert(this.exists(id), "The node does not exist!");
            peerNode = this.manager.getById(id);

            wrapperCallback = function (event) {
                delete event.data.type;
                callback.call(scope || peerNode.getProperties(), event.data);
            };

            peerNode.addEventListener("peerEvent", wrapperCallback, scope);

            if (this._supportsAction(peerNode.getType())) {
                actionId = peerNode.getProperty("actionId");
                actionNode = this.actionService._actionById(actionId);

                newCallback = function (event) {
                    delete event.data.type;
                    callback.call(scope || actionNode.getProperties(), event.data);
                };

                actionNode.addEventListener("peerEvent", newCallback, scope);
            }

            return {
                remove: function () {
                    peerNode.removeEventListener("peerEvent", wrapperCallback, scope);
                    if (this._supportsAction(peerNode.getType())) {
                        actionNode.removeEventListener("peerEvent", newCallback, scope);
                    }
                }.bind(this)
            };
        },
        /**
         * Adds a callback to be triggered whenever an event is fired either from this component or the action linked with this component
         * @param id {string}  widgetId
         *
         * @example
         * uiBuilder.addPropertySetCallback(myButtonId, function (eventData) {
         *     console.log(eventData);
         * });
         * //Providing scope
         * uiBuilder.addPropertySetCallback(myButtonId, function (eventData) {
         *     console.log(eventData);
         * }, someObj);
         * @param callback  {function} The function to be fired when any event is triggered.
         * @param scope  {object} The scope in which the callback will run.
         * @instance
         * @returns {object}  You should save this in a variable. This object has a method "remove" that can be used to clean up this callback.
         */

        addPropertySetCallback: function (id, callback, scope) {
            var actionId, peerNode, wrapperCallback, actionWrapperCallback;

            Log.assert(this.exists(id), "The node does not exist!");
            peerNode = this.manager.getById(id);

            wrapperCallback = function (event) {
                if (event.originator && event.originator.source === "UI") {
                    callback.call(scope || peerNode.getProperties(), {
                        property: event.data.key,
                        oldValue: event.data.oldValue,
                        newValue: event.data.newValue
                    });
                }
            };

            actionWrapperCallback = function (data, originator) {
                if (originator && originator.source === "UI") {
                    callback.call(scope, data);
                }
            };

            peerNode.addEventListener("propertySet", wrapperCallback, scope);

            if (this._supportsAction(peerNode.getType())) {
                actionId = peerNode.getProperty("actionId");
                this.actionService.addActionChangeCallback(actionId, actionWrapperCallback, scope);
            }

            return {
                remove: function () {
                    peerNode.removeEventListener("propertySet", wrapperCallback, scope);
                    if (this._supportsAction(peerNode.getType())) {
                        this.actionService.removeActionChangeCallback(actionId);
                    }
                }.bind(this)
            };
        },
        /**
         * Returns the first component's id that matches the given tag. This method is useful when you have components defined in the config and know the tag but want to get to the id.
         *
         * @example
         * var sectionId = uiBuilder.tagToId("mySectionTag")
         * @param tag {string} The tag of the component
         * @instance
         * @returns {string} The Id of the component
         **/

        tagToId: function (tag) {
            if (tag) {
                return this.tagToPeerNodeIdMap[tag];
            }
        },

        /**
         *
         * @param config
         * @private
         */

        createPopupsFromConfig: function (config) {
            config.popups.forEach(function (item) {
                this._addConfigItem(item);
            }, this);
        },
        /**Builds a Gallery popup using the information in the gallery config object.
         *
         * @example
         * uiBuilder.createGalleryPopupFromConfig(MyGalleryConfig)
         * @param config
         * @instance
         * @returns undefined
         */

        createGalleryPopupFromConfig: function (config) {
            this._addConfigItem(config);
        },

        /**
         * Builds an empty toolstrip using the information in the toolstrip config object. It does not do a recursive build of the toolstrip children, that are tab groups etc.
         *
         * @example
         * uiBuilder.addToolstripFromConfig(MyToolstripConfig)
         * @param config {object} The object that should be constructed
         * @instance
         * @returns undefined
         */
        addToolstripFromConfig: function (config) {
            this.create(config);
        },

        /**
         * Builds and adds a tab group with the specified tag to the Toolstrip. The tab and all it's
         * descendants are added recursively. The required data about the tab and it's descendants
         * is fetched from the toolstrip configuration object.
         *
         * @example
         * uiBuilder.addTabGroupFromConfig(MyToolstripConfig, "myTabGroupTab", "myToolstripTag")
         * @param config {object}  The object that should be constructed
         * @param tabGroupTag {string} The tag of the tab group that needs to be found and added recursively
         * to the toolstrip.
         * @param toolstripTag {string} The tag of the toolstrip
         * @instance
         * @returns undefined
         */
        addTabGroupFromConfig: function (config, tabGroupTag, toolstripTag) {
            this._addConfigItem(this._findInConfig(config, tabGroupTag), toolstripTag);
        },

        /**
         * Builds and adds a status bar from an object that specifies the bar and all of its children.
         *
         * @param config {object}  The object that should be constructed
         * @param statusBarTag {string} The tag of the status bar to be constructed
         * @instance
         * @returns undefined
         */
        addStatusBarFromConfig: function (config, statusBarTag) {
            this._addConfigItem(this._findInConfig(config, statusBarTag));
        },

        /**
         * Recursively adds the supplied configuration item and it's descendants.
         * @private
         */
        _addConfigItem: function (item, parentTag) {
            if (!registry.byId(item.id)) {
                if (parentTag) {
                    item.parentId = this.tagToId(parentTag);
                }
                item.id = this.create(item);
                // If the item has children then repeat the above steps for all the descendants of
                // the configuration item.
                if (item.children) {
                    this._addChildren(item.id, item.children);
                }
            } else {
                this.add(item.id, item.parentId);
            }
        },

        /**
         * Helper method which Iterates through the children of the supplied configuration item and
         * calls addConfigItem on them.
         * @param parentId {string} The id of the parent of the children to be added.
         * @param children {object[]} An array of children that need to be added.
         * @private
         */
        _addChildren: function (parentId, children) {
            children.forEach(function (child) {
                // Assigns a parentId to every child which is later used to add/remove it from the
                // view.
                child.parentId = parentId;
                // Call the addConfigItem method for each child, traversing the configuration tree
                // in a Depth First manner.
                this._addConfigItem(child);
            }, this);
        },

        /**
         * Helper method which is used to find a particular item in the configuration object,
         * using the supplied tag of the item.
         * @param config
         * @param tag {string} The tag of the item that needs to be found in the toolstrip
         * configuration object.
         * @private
         */
        _findInConfig: function (config, tag) {
            var result;
            // A recursive search for the item which has the supplied tag.
            function getItem(item, tag) {
                if (item.tag === tag) {
                    result = item;
                    return;
                }

                if (item.children) {
                    item.children.forEach(function (child) {
                        getItem(child, tag);
                    });
                }
            }

            // Initiate the recursive search by supplying the root Toolstrip configuration item.
            getItem(config, tag);
            // Return the configuration item which matches the given tag.
            return result;
        },

//TODO: Maybe add a remove Event Listener

        /**
         * Destroys the toolstrip peer node along with all its children.
         * @private
         */
        cleanup: function () {
            if (!this.isMATLABDriven) {
                this._getRootNodeByType(TypeConstants.ROOT).destroy();
            }
            this.actionService.destroy();
            PeerModelManager.cleanup(this._channel);
        },

        /**
         *
         * @param type
         * @private
         */
        _getRootNodeByType: function (type) {
            var result = null;
            
            // Lookup in the cached map first to prevent expensive lookup in peer model tree
            if (this._typeToRootNodeMap[type]) {
                result = this._typeToRootNodeMap[type];
            } else {
                var nodes = this.manager.getByType(type);
                if (nodes) {
                    result = nodes[0];
                }
                this._typeToRootNodeMap[type] = result;
            }
            
            return result;
        },

        /**
         * Return all category model objects sorted in descending order based on the index property.
         * @returns {CategoryModel[]}
         * @private
         */
        getAllCategoriesInDescendingOrder: function (galleryPopupNode) {
            var i, result = [], ascending = galleryPopupNode.getChildren();

            for (i = ascending.length - 1; i >= 0; --i) {
                result.push(ascending[i]);
            }
            
            return result;
        },
        /**
         *
         * @param galleryPopupTag
         * @returns {object}
         * @private
         */

        getGalleryState: function (galleryPopupTag) {
            var allFavorites, allItemsInGalleryPopup = [], state, galleryPopupPeerNode, favCategoryPeerNode,
                galleryPopupId = this.tagToId(galleryPopupTag);

            // The tags array is an ordered array of tags of all unique items in the Gallery Popup.
            // The isFavorite array marks whether the corresponding element in the tags array is a favorite or not.
            state = {
                items: {
                    tags: [],
                    isFavorite: []
                },
                categories: {
                    tags: []
                }
            };

            if (!this.exists(galleryPopupId)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            galleryPopupPeerNode = this.manager.getById(galleryPopupId);
            favCategoryPeerNode = this.manager.getById(galleryPopupPeerNode.getProperty("favCategoryId"));

            allFavorites = favCategoryPeerNode.getChildren();

            galleryPopupPeerNode.getChildren().forEach(function (categoryPeerNode) {
                categoryPeerNode.getChildren().forEach(function (galleryItemPeerNode) {
                    allItemsInGalleryPopup.push(galleryItemPeerNode);
                });
            });

            allFavorites.concat(allItemsInGalleryPopup).forEach(function (item) {
                var tag, isFavorite, actionId = item.getProperty("actionId");
                tag = item.getProperty("tag");
                tag = tag.replace("_favorite", "");
                isFavorite = item.getProperty("isFavorite");
                if (state.items.tags.indexOf(tag) === -1) {
                    state.items.tags.push(tag);
                    state.items.isFavorite.push(isFavorite);
                }
            }, this);

            state.categories.tags = galleryPopupPeerNode.getChildren().map(function (category) {
                return category.getProperty("tag");
            });

            galleryPopupPeerNode.setProperty("state", state);

            return state;
        },
        /**
         *
         * @param galleryPopupTag
         * @param state
         * @private
         */

        setGalleryState: function (galleryPopupTag, state) {
            var galleryPopupPeerNode, matchingItems, categoryId, favCategoryId, favCategoryPeerNode, tag, iconOverride, textOverride, 
                descriptionOverride, count = 0, galleryPopupId = this.tagToId(galleryPopupTag), originator = {source: "setGalleryState"};

            if (!this.exists(galleryPopupId)) {
                Log.assert(false, "The node does not exist!");
                return;
            }

            galleryPopupPeerNode = this.manager.getById(galleryPopupId);
            favCategoryId = galleryPopupPeerNode.getProperty("favCategoryId");
            favCategoryPeerNode = this.manager.getById(favCategoryId);

            // Clear all current favorites
            favCategoryPeerNode.getChildren().forEach(function (item) {
                tag = item.getProperty("tag");
                tag = tag.replace("_favorite", "");

                // Do not destroy the favorite if it is a new item added by the author and was not in the user's state.
                if (state.items.tags.indexOf(tag) === -1) {
                    return;
                }

                this._peerNodeByTag(tag).setProperty("isFavorite", false);
                item.destroy(originator);
            }, this);

            // Load the favorites from user's state
            state.items.tags.forEach(function (tag, i) {
                var itemId;

                // If the tag was not a favorite in the user's state then do nothing.
                if (!state.items.isFavorite[i]) {
                    return;
                }

                matchingItems = this.manager.getByProperty("tag", tag, galleryPopupPeerNode);

                if (matchingItems.length > 0) {

                    itemId = this.create({
                        type:  matchingItems[0].getType(),
                        tag: tag + "_favorite",
                        actionId: matchingItems[0].getProperty("actionId"),
                        iconOverride: matchingItems[0].getProperty("iconOverride"),
                        textOverride: matchingItems[0].getProperty("textOverride"),
                        descriptionOverride: matchingItems[0].getProperty("descriptionOverride")
                    });

                    this.add(itemId, favCategoryId, count, originator);
                    count += 1;

                    this._setupNotInActionPropertiesSyncCallback(matchingItems[0], itemId);

                    this.set(itemId, "isFavorite", true);
                    matchingItems[0].setProperty("isFavorite", true);
                }

            }, this);

            // Move categories to correct location
            state.categories.tags.forEach(function (tag, i) {
                categoryId = this.tagToId(tag);
                if (this.exists(categoryId)) {
                    this.move(categoryId, galleryPopupId, i, originator);
                }
            }, this);
        },

        getQABState: function (toolstripTag) {
            var state = [];
            var QABId = this.get(this.tagToId(toolstripTag), "QABId");
            var widget, actionId, action;
            
            this.getChildrenIds(QABId).forEach(function (qaGroupId) {
                this.getChildrenIds(qaGroupId).forEach(function (controlId, index) {
                    var item = {};
                    item.tag = this.get(controlId, "tag");
                    item.showText = this.get(controlId, "showText");
                    item.index = index;

                    widget = registry.byId(controlId);
                    if (widget) {
                        actionId = widget.get("actionId");
                        if (actionId) {
                            action = this.actionService.getAction(actionId);
                            if (action && action.groupIdForLazyLoadingQABItems) {
                                item.groupIdForLazyLoadingQABItems = action.groupIdForLazyLoadingQABItems;
                            }
                        }
                    }
                    action = null;
                    actionId = null;
                    widget = null;

                    state.push(item);
                }, this);
            }, this);

            return state;
        },

        setQABState: function (toolstripTag, state, defaultConfig, fromDefaultControls) {
            var QABId = this.get(this.tagToId(toolstripTag), "QABId");  
            
            // Converting the object to array for backward compatibility. The earlier implementation had
            // state as an object. That didn't allow for the order of the QAB to be saved. See 1484700
            var isNewImpl = Array.isArray(state);
            if (!isNewImpl && state !== undefined) {
                var arr = [];
                Object.keys(state).forEach(function (tag) {
                    var item = {};
                    item.tag = tag;
                    item.showText = state[tag].showText;
                    arr.push(item);
                });
                state = (arr.length > 0) ? arr : undefined;
            }
            
            // We can't support the case where an empty array/object is passed in to restore defaults, since
            // this results in the defaults being restored when all of the items are removed
            if (state === undefined) {
                defaultConfig.forEach(function (obj) {
                    var id = this.create(obj);
                    this.set(id, "isInQAB", true);
                    this._hookupRemoveFromQABListener(id);
                }, this);
            } else {
                state.forEach(function (item) {
                    var tag = item.tag;
                    var isOneOfTheDefaults = false;
                    var showText = !!item.showText;
                    var index = item.index;
                    var groupIdForLazyLoadingQABItems = item.groupIdForLazyLoadingQABItems;

                    defaultConfig.forEach(function (obj) {
                        if (obj.tag === tag) {
                            isOneOfTheDefaults = true;
                            obj.showText = showText;
                            var id = this.create(obj);
                            this.set(id, "isInQAB", true);
                            this._hookupRemoveFromQABListener(id);
                        }
                    }, this);

                    if (!isOneOfTheDefaults) {
                        if (groupIdForLazyLoadingQABItems && !fromDefaultControls) {
                            // setQABState method only gets called during startup or when defaultControls are
                            // changed. The lazyLoadingQABItemsMap should only be updated on startup. We do not need
                            // to update it if the setQABState method is calling for making any change for the
                            // default controls.
                            if (!this.lazyLoadingQABItemsMap[groupIdForLazyLoadingQABItems]) {
                                this.lazyLoadingQABItemsMap[groupIdForLazyLoadingQABItems] = [];
                            }
                            this.lazyLoadingQABItemsMap[groupIdForLazyLoadingQABItems].push({
                                tag: tag,
                                showText: showText,
                                index: index
                            });
                        } else {
                            var tagOfCorrespondingControlInToolstrip = tag.replace("_qab", "");
                            var idOfCorrespondingControlInToolstrip = this.tagToId(tagOfCorrespondingControlInToolstrip);

                            if (idOfCorrespondingControlInToolstrip &&
                                (Object.keys(ComponentsEligibleForQAB).indexOf(this.get(idOfCorrespondingControlInToolstrip, "type")) > -1) &&
                                this.get(idOfCorrespondingControlInToolstrip, "isInQAB") === false) {

                                var id = this._createCorrespondingComponentInQAB(idOfCorrespondingControlInToolstrip);
                                this.set(id, "showText", showText);
                                this.set(idOfCorrespondingControlInToolstrip, "isInQAB", true);
                            } else {
                                var idOfQABControl = this.tagToId(tag);
                                if (idOfQABControl) {
                                    this.set(idOfQABControl, "showText", showText);
                                }
                            }
                        }
                    }
                }, this);
            }
        },

        /**
         * Adds the corresponding items to QAB for items that are lazily loaded within the toolstrip.
         * @param {String} groupId
         * @instance
         * @returns undefined
         */
        lazyLoadQABItems: function (groupId) {
            if (this.lazyLoadingQABItemsMap && this.lazyLoadingQABItemsMap[groupId]) {
                this.lazyLoadingQABItemsMap[groupId].forEach(function (item) {
                    var tagOfCorrespondingControlInToolstrip = item.tag.replace("_qab", "");
                    var idOfCorrespondingControlInToolstrip = this.tagToId(tagOfCorrespondingControlInToolstrip);

                    if (idOfCorrespondingControlInToolstrip &&
                        (Object.keys(ComponentsEligibleForQAB).indexOf(this.get(idOfCorrespondingControlInToolstrip, "type")) > -1) &&
                        this.get(idOfCorrespondingControlInToolstrip, "isInQAB") === false) {

                        var id = this._createCorrespondingComponentInQAB(idOfCorrespondingControlInToolstrip, null, item.index);
                        this.set(id, "showText", item.showText);
                        this.set(idOfCorrespondingControlInToolstrip, "isInQAB", true);

                        delete this.lazyLoadingQABItemsMap[groupId];
                    }
                }.bind(this));
            }
        }
    };

    return declare([_PeerModelListenerMixin], modelManager);
});


